import React from 'react'
import { Button, Col, Row } from 'react-bootstrap'
import { Listing, ListingColumn, SectionStyles } from '../ui/Listing/Listing'
import { Loading } from '../ui/Loading/Loading'

import { Invoice, WipClientWithJobsAndLatestInvoice, WipData, WipTimeWithoutJob } from '../../../../back-end/utilities/apiDefinitions'

import { InvoiceTime } from '../../../../back-end/common/invoiceTime'
import { FormText } from '../ui/Form/Text'
import { roundToTwo } from '../../utilities/format'
import { DateTime } from 'luxon'
import { InvoiceEstimate } from '../../../../back-end/common/invoiceEstimate'
import { RowTypes } from '../../screens/invoice/invoiceAllocation'
import { invoiceState } from '../../utilities/invoice'
import { ArrowRightCircle, Edit } from 'react-feather'
import { InvoiceCost } from '../../../../back-end/common/invoiceCost'

interface WipAllocationRow {
	id: string
	type: RowTypes
	groupName?: string
	// children only
	date?: string
	employee?: string
	task?: string
	comments?: string
	agedWip?: number
	currentWip?: number
	wip?: number
	writeOff?: number
	invoice?: number
	carryForward?: number
	onClick?: unknown
	children?: WipAllocationRow[]
}

type JobRow = {
	jobID: string
	xpmID: string
	name: string
	number: string
	timeItems: WipTimeWithoutJob[]
}

const isTime = (item: WipAllocationRow) => item.type === 'time' || item.type === 'estimate' || item.type === 'cost'
const isJob = (item: WipAllocationRow) => item.type === 'job'

type ReduceableTimeKey = keyof Pick<WipAllocationRow, 'agedWip' | 'currentWip' | 'wip' | 'writeOff' | 'invoice' | 'carryForward'>

const reduceWipArray = (wip: WipAllocationRow[], types: RowTypes[], key: ReduceableTimeKey) =>
	wip.reduce((total, row) => (types.includes(row.type) ? (total += row[key] || 0) : total), 0)

const clientJobMap = (
	j: JobRow,
	invoiceTimeData: Pick<InvoiceTime, 'invoiceID' | 'timeID' | 'amountBilled' | 'writtenOff'>[],
	invoiceCostData: Pick<InvoiceCost, 'invoiceID' | 'costID' | 'amountBilled' | 'writtenOff'>[],
	invoiceEstimateData: Pick<InvoiceEstimate, 'invoiceID' | 'estimateID' | 'amountBilled' | 'writtenOff'>[],
	restrictedDate: string
): WipAllocationRow[] => {
	const timeRows = j.timeItems
		.filter((t) => DateTime.fromISO(t.date).diff(DateTime.fromISO(restrictedDate), ['days']).days <= 0)
		.map((t) => {
			const invoiceTimeRecord = invoiceTimeData.find((it) => it.timeID === t.timeID)
			const invoiceCostRecord = invoiceCostData.find((ic) => ic.costID === t.costID)
			const invoiceEstimateRecord = invoiceEstimateData.find((ie) => ie.estimateID === t.estimateID)
			const writtenOff = invoiceTimeRecord?.writtenOff || invoiceCostRecord?.writtenOff || invoiceEstimateRecord?.writtenOff
			const amountBilled = invoiceTimeRecord?.amountBilled || invoiceCostRecord?.amountBilled || invoiceEstimateRecord?.amountBilled
			const row: WipAllocationRow = {
				id: t.timeID || t.costID || t.estimateID || '',
				type: t.timeID ? 'time' : t.costID ? 'cost' : 'estimate',
				date: t.date,
				employee: t.staff.name,
				task: t.task.name,
				comments: t.note || '',
				agedWip: t.agedValue,
				currentWip: t.currentValue,
				wip: t.remainingDollars,
				writeOff: writtenOff || 0,
				invoice: amountBilled || 0,
				carryForward: Math.max(0, t.remainingDollars - (writtenOff || 0) - (amountBilled || 0)),
			}
			return row
		})
	const allChildrenAreEstimates = timeRows.every((t) => t.type === 'estimate')
	return [
		{
			id: j.jobID,
			type: allChildrenAreEstimates ? 'estimateGroup' : 'job',
			groupName: allChildrenAreEstimates ? 'Estimates' : j.number + ' - ' + j.name,
			agedWip: reduceWipArray(timeRows, ['time', 'cost', 'estimate'], 'agedWip'),
			currentWip: reduceWipArray(timeRows, ['time', 'cost', 'estimate'], 'currentWip'),
			wip: reduceWipArray(timeRows, ['time', 'cost', 'estimate'], 'wip'),
			writeOff: reduceWipArray(timeRows, ['time', 'cost', 'estimate'], 'writeOff'),
			invoice: reduceWipArray(timeRows, ['time', 'cost', 'estimate'], 'invoice'),
			carryForward: reduceWipArray(timeRows, ['time', 'cost', 'estimate'], 'carryForward'),
			children: timeRows.sort((a, b) => Date.parse(a.date!) - Date.parse(b.date!)),
		},
	]
}

const getUniqueJobsFromWip = (client: WipClientWithJobsAndLatestInvoice) => {
	return client.jobs
		.map((j) => ({ jobID: j.jobID, xpmID: j.xpmID, name: j.name, number: j.number, timeItems: j.timeItems })) // just keep the stuff we need
		.reduce((jobs, job) => {
			const jobIndex = jobs.findIndex((j) => j.jobID === job.jobID)
			if (jobIndex < 0) {
				jobs.push(job)
			} else {
				jobs[jobIndex].timeItems = jobs[jobIndex].timeItems.concat(job.timeItems)
			}
			return jobs
		}, [] as JobRow[]) // get unique jobs and merge time items
}

const mapWipData = (
	wipData: WipData,
	invoiceTimeData: Pick<InvoiceTime, 'invoiceID' | 'timeID' | 'amountBilled' | 'writtenOff'>[],
	invoiceEstimateData: Pick<InvoiceEstimate, 'invoiceID' | 'estimateID' | 'amountBilled' | 'writtenOff'>[],
	invoiceCostData: Pick<InvoiceCost, 'invoiceID' | 'costID' | 'amountBilled' | 'writtenOff'>[],
	restrictedDate: string
): WipAllocationRow[] => {
	if (wipData.grouping === 'fcje') {
		return wipData.familyGroups.flatMap((fg) =>
			fg.clients.map((c) => {
				const jobs = getUniqueJobsFromWip(c)
				const children = jobs.flatMap((j) => clientJobMap(j, invoiceTimeData, invoiceCostData, invoiceEstimateData, restrictedDate))

				return {
					id: c.clientID,
					type: 'client',
					groupName: c.name,
					date: '',
					employee: '',
					task: '',
					comments: '',
					agedWip: reduceWipArray(children, ['job'], 'agedWip'),
					currentWip: reduceWipArray(children, ['job'], 'currentWip'),
					wip: reduceWipArray(children, ['job'], 'wip'),
					writeOff: reduceWipArray(children, ['job'], 'writeOff'),
					invoice: reduceWipArray(children, ['job'], 'invoice'),
					carryForward: reduceWipArray(children, ['job'], 'carryForward'),
					children: children,
				}
			})
		)
	} else {
		// wipData.grouping === 'fjcj'
		return wipData.familyGroups.flatMap((fg) =>
			fg.jobTypes.map((j) => {
				const children = j.clients.flatMap((c): WipAllocationRow[] => {
					const jobs = getUniqueJobsFromWip(c)
					const jobRows = jobs.flatMap((j) => clientJobMap(j, invoiceTimeData, invoiceCostData, invoiceEstimateData, restrictedDate))

					return [
						{
							id: c.clientID,
							type: 'client',
							groupName: c.name,
							agedWip: reduceWipArray(jobRows, ['job'], 'agedWip'),
							currentWip: reduceWipArray(jobRows, ['job'], 'currentWip'),
							wip: reduceWipArray(jobRows, ['job'], 'wip'),
							writeOff: reduceWipArray(jobRows, ['job'], 'writeOff'),
							invoice: reduceWipArray(jobRows, ['job'], 'invoice'),
							carryForward: reduceWipArray(jobRows, ['job'], 'carryForward'),
						},
						...jobRows,
					]
				})

				return {
					id: j.type || 'Job Type',
					type: 'jobType',
					groupName: j.type || 'Job Type',
					date: '',
					employee: '',
					task: '',
					comments: '',
					agedWip: reduceWipArray(children, ['job'], 'agedWip'),
					currentWip: reduceWipArray(children, ['job'], 'currentWip'),
					wip: reduceWipArray(children, ['job'], 'wip'),
					writeOff: reduceWipArray(children, ['job'], 'writeOff'),
					invoice: reduceWipArray(children, ['job'], 'invoice'),
					carryForward: reduceWipArray(children, ['job'], 'carryForward'),
					children: children,
				}
			})
		)
	}
}

const listingStyle: SectionStyles = {
	trDepth0: { background: '#8AA6FF40' },
}

interface ListingScreenProps {
	isLoading: boolean
	invoiceData: Invoice | null
	wipData: WipData | null
	invoiceTimeData: Pick<InvoiceTime, 'invoiceID' | 'timeID' | 'amountBilled' | 'writtenOff'>[] | null
	invoiceEstimateData: Pick<InvoiceEstimate, 'invoiceID' | 'estimateID' | 'amountBilled' | 'writtenOff'>[] | null
	invoiceCostData: Pick<InvoiceCost, 'invoiceID' | 'costID' | 'amountBilled' | 'writtenOff'>[] | null
	onUpdateTime: (type: RowTypes, id: string, field: keyof Pick<WipAllocationRow, 'writeOff' | 'invoice'>, value: number) => void
	columnWidths: Record<string, string>
	handleEstimateClick: (estimateId?: string) => void
	handleAddJobClick: () => void
	restrictedDate: string
}

const ListingScreen = (props: ListingScreenProps) => {
	const wipData = React.useMemo(() => {
		if (props.wipData && props.invoiceTimeData && props.invoiceEstimateData && props.invoiceCostData) {
			return mapWipData(props.wipData, props.invoiceTimeData, props.invoiceEstimateData, props.invoiceCostData, props.restrictedDate)
		}
		return []
	}, [props.wipData, props.invoiceTimeData, props.invoiceEstimateData, props.invoiceCostData, props.restrictedDate])

	const getUpdateFunction = (item: WipAllocationRow, field: keyof Pick<WipAllocationRow, 'writeOff' | 'invoice'>) => {
		return (e: { target: { value: string } }) => props.onUpdateTime(item.type, item.id, field, Number(e.target.value))
	}

	const state = invoiceState(props.invoiceData!.status, props.invoiceData!.sentToContact)
	const canEdit = state === 'draft' || state === 'awaiting-approval'
	const columns = getColumns(props, getUpdateFunction, props.handleEstimateClick, canEdit)

	return (
		<>
			<Row>
				<Col>
					{props.isLoading ? (
						<div className="center-flex p-5">
							<Loading show />
						</div>
					) : (
						<Listing<WipAllocationRow>
							list={wipData}
							initialExpanded={wipData.reduce(
								(expanded, wip) => ({ ...expanded, [wip.id]: true, ...wip.children?.reduce((expC, c) => ({ ...expC, [c.id]: true }), {}) }),
								{}
							)}
							columns={columns}
							customButtons={[
								<Button key="estimate" onClick={() => props.handleEstimateClick()} disabled={!canEdit}>
									Add Estimate
								</Button>,
								<Button key="job" onClick={props.handleAddJobClick} disabled={!canEdit}>
									Add Other Job
								</Button>,
							]}
							name="WIP Record"
							namePlural="WIP Records"
							getIDFunc={(item) => item.id}
							defaultSort={{ column: 'id', order: 'ASC' }}
							isLoading={props.isLoading}
							paginationControls="none"
							sectionStyles={listingStyle}
							borderRadius
							boxShadow
							headerPadRow
							hideDisplayCount
							responsive
						/>
					)}
				</Col>
			</Row>
		</>
	)
}

const getColumns = (
	props: ListingScreenProps,
	getUpdateFunction: (item: WipAllocationRow, field: keyof Pick<WipAllocationRow, 'writeOff' | 'invoice'>) => (e: { target: { value: string } }) => void,
	handleEstimateClick: (estimateId?: string) => void,
	canEdit: boolean
) => {
	const fjcjCol: ListingColumn<WipAllocationRow>[] = [
		{
			value: (item) => item.groupName || '',
			render: (item) => <div className="listing-group">{item.groupName}</div>,
			colSpan: () => (props.wipData?.grouping === 'fjcj' ? 7 : 0),
			hidden: (item) => item.type !== 'jobType',
			showHeader: true,
			headerText: 'Job Category',
			sortColumnName: 'jobCategory',
			width: props.columnWidths.jobType,
		},
	]

	const commonCols: ListingColumn<WipAllocationRow>[] = [
		{
			value: (item) => item.groupName || '',
			render: (item) => <div className="listing-group">{!isJob(item) ? item.groupName : ''}</div>,
			colSpan: (item) => (isTime(item) || isJob(item) ? 1 : 6),
			hidden: (item) => (props.wipData?.grouping === 'fcje' && item.type !== 'client') || (props.wipData?.grouping === 'fjcj' && item.type === 'jobType'),
			showHeader: true,
			headerText: 'Client',
			sortColumnName: 'client',
			width: props.columnWidths.client,
		},
		{
			value: (item) => item.groupName || '',
			render: (item) => <div className="listing-group">{item.groupName}</div>,
			colSpan: (item) => (isTime(item) ? 2 : 5),
			hidden: (item) => item.type === 'client' || item.type === 'jobType',
			showHeader: true,
			headerText: 'Assignment',
			sortColumnName: 'assignment',
			width: props.columnWidths.assignment,
		},
		{
			value: (item) => item.date || '',
			render: (item) => <div>{DateTime.fromISO(item.date!).toFormat('yyyy-MM-dd')}</div>,
			hidden: (item) => !isTime(item),
			showHeader: true,
			headerText: 'Date',
			sortColumnName: 'date',
			width: props.columnWidths.date,
		},
		{
			value: (item) => item.employee || '',
			render: (item) => <div>{item.employee}</div>,
			hidden: (item) => !isTime(item),
			showHeader: true,
			headerText: 'Employee',
			sortColumnName: 'employee',
			width: props.columnWidths.employee,
		},
		{
			value: (item) => item.task || '',
			render: (item) => <div>{item.task}</div>,
			hidden: (item) => !isTime(item),
			showHeader: true,
			headerText: 'Task',
			sortColumnName: 'task',
			width: props.columnWidths.task,
		},
		{
			value: (item) => item.comments || '',
			render: (item) => (
				<>
					{item.comments}
					{item.type === 'estimate' && canEdit && (
						<>
							<Edit
								onClick={() => handleEstimateClick(item.id)}
								style={{ marginLeft: '15px', verticalAlign: 'sub', cursor: 'pointer' }}
								size={20}
							>
								Edit
							</Edit>
						</>
					)}
				</>
			),
			hidden: (item) => !isTime(item),
			showHeader: true,
			headerText: 'Comments',
			sortColumnName: 'comments',
			width: props.columnWidths.comments,
		},
		{
			value: (item) => item.agedWip?.toFixed(2) || '0.00',
			render: (item) => {
				const value = roundToTwo(item.agedWip || 0).toLocaleString()
				const isOldEstimate = (item.type === 'estimate' || item.type === 'estimateGroup') && (item.agedWip || 0) > 0
				return <div className="text-end">{item.agedWip ? (isOldEstimate ? `(${value})` : value) : '-'}</div>
			},
			showHeader: true,
			headerText: 'Aged Value',
			sortColumnName: 'agedWip',
			sortJustify: 'end',
			width: props.columnWidths.agedWip,
		},
		{
			value: (item) => item.currentWip?.toFixed(2) || '0.00',
			render: (item) => <div className="text-end">{item.currentWip ? roundToTwo(item.currentWip).toLocaleString() : '-'}</div>,
			showHeader: true,
			headerText: 'Current Value',
			sortColumnName: 'currentWip',
			sortJustify: 'end',
			width: props.columnWidths.currentWip,
		},
		{
			value: (item) => item.wip?.toFixed(2) || '0.00',
			render: (item) => {
				const value = roundToTwo(item.wip || 0).toLocaleString()
				const isOldEstimate = (item.type === 'estimate' || item.type === 'estimateGroup') && (item.agedWip || 0) > 0
				return <div className="text-end">{isOldEstimate ? `(${value})` : value}</div>
			},
			showHeader: true,
			headerText: 'Total Value',
			sortColumnName: 'wip',
			sortJustify: 'end',
			width: props.columnWidths.wip,
		},
		{
			value: (item) => item.writeOff?.toFixed(2) || '0.00',
			render: (item) => (
				<div className="d-flex">
					<ArrowRightCircle
						role="button"
						style={{ zIndex: 10 }}
						onClick={() =>
							getUpdateFunction(
								item,
								'writeOff'
							)({ target: { value: String((item.agedWip || 0) + (item.currentWip || 0) - (item.invoice || 0)) } })
						}
					/>
					<FormText
						key={`${item.id}-writeOff`}
						placeholder="0"
						type="number"
						name={`${item.id}-writeOff`}
						className={'text-end' + (roundToTwo(item.writeOff || 0) < 0 ? ' is-invalid' : '')}
						value={roundToTwo(item.writeOff || 0)}
						onChange={getUpdateFunction(item, 'writeOff')}
						disabled={!canEdit}
					/>
				</div>
			),
			showHeader: true,
			headerText: 'Write-off',
			sortColumnName: 'writeOff',
			width: props.columnWidths.writeOff,
		},
		{
			value: (item) => item.invoice?.toFixed(2) || '0.00',
			render: (item) =>
				(item.type === 'estimate' && (item.currentWip || 0) > 0) || (item.type === 'estimateGroup' && (item.currentWip || 0) > 0) ? (
					<div className="text-end">{roundToTwo(item.invoice || 0).toLocaleString()}</div>
				) : (
					<div className="d-flex">
						<ArrowRightCircle
							role="button"
							style={{ zIndex: 10 }}
							onClick={() =>
								getUpdateFunction(
									item,
									'invoice'
								)({ target: { value: String((item.agedWip || 0) + (item.currentWip || 0) - (item.writeOff || 0)) } })
							}
						/>
						<FormText
							key={`${item.id}-invoice`}
							placeholder="0"
							type="number"
							name={`${item.id}-invoice`}
							className={'text-end' + (roundToTwo(item.invoice || 0) < 0 ? ' is-invalid' : '')}
							value={roundToTwo(item.invoice || 0)}
							onChange={getUpdateFunction(item, 'invoice')}
							disabled={!canEdit}
						/>
					</div>
				),
			showHeader: true,
			headerText: 'Invoice',
			sortColumnName: 'invoice',
			width: props.columnWidths.invoice,
		},
		{
			value: (item) => item.carryForward?.toFixed(2) || '0.00',
			render: (item) => {
				const value = roundToTwo(item.carryForward || 0).toLocaleString()
				const isEstimate = item.type === 'estimate' || item.type === 'estimateGroup'
				return <div className="text-end">{isEstimate ? `(${value})` : value}</div>
			},
			showHeader: true,
			headerText: 'Carry Fwd',
			sortColumnName: 'carryForward',
			width: props.columnWidths.carryForward,
		},
	]

	return (props.wipData?.grouping === 'fjcj' ? fjcjCol : []).concat(commonCols)
}

export default ListingScreen
